import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { SortOrder } from '@els/els-react--table';
import { COMMERCIAL_SUPPLIER_SORT_DIRECTIONS } from './constants';
import SubstanceCommercialSupplierSortHeaderButton, {
  getCommercialSupplierAriaSort,
  getCommercialSupplierSortAnnouncement,
} from './SubstanceCommercialSupplierSortHeaderButton';

function getSortDirection(sortOrder) {
  if (sortOrder === SortOrder.ASC) {
    return COMMERCIAL_SUPPLIER_SORT_DIRECTIONS.asc;
  }

  if (sortOrder === SortOrder.DESC) {
    return COMMERCIAL_SUPPLIER_SORT_DIRECTIONS.desc;
  }

  return COMMERCIAL_SUPPLIER_SORT_DIRECTIONS.neutral;
}

function getNextSortOrder(columnValue, { sortBy, sortOrder }) {
  if (sortBy !== columnValue || sortOrder === SortOrder.NEUTRAL) {
    return SortOrder.ASC;
  }

  if (sortOrder === SortOrder.ASC) {
    return SortOrder.DESC;
  }

  return SortOrder.NEUTRAL;
}

function isSortableColumn(columnValue, { isSortable, sortableColumns }) {
  return isSortable && (Object.keys(sortableColumns).length === 0 || sortableColumns[columnValue] !== undefined);
}

function SubstanceCommercialSupplierTableHead({ rowProps, sortingProps, onSortAnnouncement }) {
  const handleSort = (column) => {
    const nextSortOrder = getNextSortOrder(column.value, sortingProps);
    const nextSortDirection = getSortDirection(nextSortOrder);

    onSortAnnouncement(getCommercialSupplierSortAnnouncement(column.label, nextSortDirection));
    sortingProps.updateSortBy(column.value, nextSortOrder);
  };

  return (
    <tr className="table__row c-els-table__row table__row--head c-els-table__row--head">
      {rowProps.columns.map((column) => {
        const sortable = isSortableColumn(column.value, sortingProps);
        const isActive = sortable && sortingProps.sortBy === column.value && sortingProps.sortOrder !== SortOrder.NEUTRAL;
        const sortDirection = getSortDirection(sortingProps.sortOrder);

        return (
          <th
            key={column.value}
            scope="col"
            className={classnames(
              'table__cell',
              'c-els-table__cell',
              'c-els-table__basic-cell',
              'table__cell--header',
              'c-els-table__cell--header',
              {
                'c-els-table__cell--icon-only': column.iconOnly,
                'c-els-table__cell--sort-active': isActive,
              }
            )}
            style={{ width: column.width }}
            aria-sort={getCommercialSupplierAriaSort(isActive, sortDirection)}>
            {column.headContentLeft}
            {sortable ? (
              <SubstanceCommercialSupplierSortHeaderButton
                dataE2e={`commercial-supplier-sort-${column.value}`}
                direction={sortDirection}
                isActive={isActive}
                label={column.label}
                className="c-els-table__sortable-button rcs-commercial-sort-button--outer"
                onSort={() => handleSort(column)}
              />
            ) : (
              column.label
            )}
            {column.headContentRight}
          </th>
        );
      })}
    </tr>
  );
}

SubstanceCommercialSupplierTableHead.propTypes = {
  rowProps: PropTypes.shape({
    columns: PropTypes.arrayOf(
      PropTypes.shape({
        headContentLeft: PropTypes.node,
        headContentRight: PropTypes.node,
        iconOnly: PropTypes.bool,
        label: PropTypes.string,
        value: PropTypes.string.isRequired,
        width: PropTypes.string,
      })
    ).isRequired,
  }).isRequired,
  sortingProps: PropTypes.shape({
    isSortable: PropTypes.bool.isRequired,
    sortBy: PropTypes.string,
    sortOrder: PropTypes.string.isRequired,
    sortableColumns: PropTypes.object.isRequired,
    updateSortBy: PropTypes.func.isRequired,
  }).isRequired,
  onSortAnnouncement: PropTypes.func.isRequired,
};

export default SubstanceCommercialSupplierTableHead;
